import { useState, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion, AnimatePresence } from "framer-motion";
import { CaretLeft, CaretRight, X, ArrowsOutSimple } from "@phosphor-icons/react";
import { captionFor } from "@/data/captions";

export default function PropertyGallery({ images = [], name = "" }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [thumbRef, thumbApi] = useEmblaCarousel({ containScroll: "keepSnaps", dragFree: true });
  const [selected, setSelected] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const idx = emblaApi.selectedScrollSnap();
    setSelected(idx);
    if (thumbApi) thumbApi.scrollTo(idx);
  }, [emblaApi, thumbApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);
  const prev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, prev, next]);

  if (!images.length) return null;

  return (
    <div data-testid="property-gallery">
      {/* Main carousel */}
      <div className="relative overflow-hidden rounded-3xl group">
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex">
            {images.map((src, i) => (
              <div key={i} className="relative flex-[0_0_100%] min-w-0">
                <button
                  onClick={() => setLightbox(true)}
                  data-testid={`gallery-slide-${i}`}
                  className="block w-full aspect-[16/10] md:aspect-[21/9] cursor-zoom-in"
                >
                  <img src={src} alt={`${name} — photo ${i + 1}`} className="w-full h-full object-cover" loading={i === 0 ? "eager" : "lazy"} />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-emerald/40 to-transparent pointer-events-none" />

        {captionFor(images[selected]) && (
          <div data-testid="gallery-caption" className="absolute bottom-4 left-4 rounded-full bg-emerald/70 backdrop-blur px-4 py-1.5 text-sm text-sand font-medium">
            {captionFor(images[selected])}
          </div>
        )}

        <button data-testid="gallery-prev" onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-sand/90 backdrop-blur grid place-items-center text-emerald hover:bg-sunset hover:text-sand transition-colors duration-300 opacity-0 group-hover:opacity-100">
          <CaretLeft size={20} weight="bold" />
        </button>
        <button data-testid="gallery-next" onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-sand/90 backdrop-blur grid place-items-center text-emerald hover:bg-sunset hover:text-sand transition-colors duration-300 opacity-0 group-hover:opacity-100">
          <CaretRight size={20} weight="bold" />
        </button>

        <div className="absolute bottom-4 right-4 flex items-center gap-3">
          <span className="rounded-full bg-emerald/70 backdrop-blur px-3 py-1.5 text-xs text-sand">{selected + 1} / {images.length}</span>
          <button data-testid="gallery-expand" onClick={() => setLightbox(true)} className="rounded-full bg-sand/90 backdrop-blur px-3 py-1.5 text-xs text-emerald hover:bg-sunset hover:text-sand transition-colors duration-300 flex items-center gap-1.5">
            <ArrowsOutSimple size={14} weight="bold" /> View all
          </button>
        </div>
      </div>

      {/* Thumbnails */}
      <div ref={thumbRef} className="overflow-hidden mt-4">
        <div className="flex gap-3">
          {images.map((src, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              data-testid={`gallery-thumb-${i}`}
              className={`relative flex-[0_0_22%] md:flex-[0_0_12%] min-w-0 aspect-[4/3] rounded-lg overflow-hidden transition-opacity duration-300 ${selected === i ? "ring-2 ring-sunset" : "opacity-55 hover:opacity-100"}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-emerald/95 backdrop-blur-sm flex flex-col"
            data-testid="gallery-lightbox"
          >
            <div className="flex justify-between items-center px-6 py-5 text-sand">
              <div className="flex items-baseline gap-3">
                <span className="font-display text-xl">{name}</span>
                {captionFor(images[selected]) && <span className="text-sand/60 text-sm">— {captionFor(images[selected])}</span>}
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sand/70 text-sm">{selected + 1} / {images.length}</span>
                <button data-testid="lightbox-close" onClick={() => setLightbox(false)} className="w-11 h-11 rounded-full border border-sand/30 grid place-items-center hover:bg-sand/10 transition-colors duration-300">
                  <X size={22} />
                </button>
              </div>
            </div>
            <div className="flex-1 relative flex items-center justify-center px-4 md:px-16 pb-6 min-h-0">
              <button onClick={prev} className="absolute left-4 md:left-6 w-12 h-12 rounded-full bg-sand/10 border border-sand/25 grid place-items-center text-sand hover:bg-sunset hover:border-sunset transition-colors duration-300 z-10">
                <CaretLeft size={22} weight="bold" />
              </button>
              <motion.img
                key={selected}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
                src={images[selected]}
                alt={`${name} — photo ${selected + 1}`}
                className="max-h-full max-w-full object-contain rounded-xl"
              />
              <button onClick={next} className="absolute right-4 md:right-6 w-12 h-12 rounded-full bg-sand/10 border border-sand/25 grid place-items-center text-sand hover:bg-sunset hover:border-sunset transition-colors duration-300 z-10">
                <CaretRight size={22} weight="bold" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
